import type { Language } from '@eformulieren/engine'
import type { Source, SourceType } from '@eformulieren/model'
import { type ReactNode, useId, useMemo, useRef, useState } from 'react'
import { inLanguage } from '../language/text.js'
import { ConfirmQuestion } from '../shell/ConfirmQuestion.js'
import { Dialog } from '../shell/Dialog.js'
import { highlight, keepEscapeInDialog } from '../shell/dialogPlace.js'
import type { CitationPlace } from './layerSources.js'
import {
  EDIT,
  EDIT_SOURCE,
  FIRST_NAME,
  LAST_NAME,
  NO_SOURCES,
  NOT_FOUND,
  type NewSource,
  REMOVE,
  SAVE_EDIT,
  SEARCH,
  SOURCE_TYPES,
  TITLE,
  TYPE,
  TYPE_LABELS,
  YEAR,
  authorYear,
  describeSource,
  matches,
} from './SourcePanel.js'

/**
 * Het paneel Bronnen: alle bronnen van het formulier, hoe vaak ze in de tekst staan, en ze beheren
 * (R-50, R-139, CitatenPOC `SourcesPanel.tsx`, ontwerp A van het invulscherm).
 *
 * **Per laag één bibliotheek**, zoals de kiezer onder een veld (`useSourcePicker`, `layerSources.ts`):
 * dezelfde bron in twee secties is één bron. Staan er in twee lagen bronnen, dan per laag een kop.
 *
 * **Wat het meer doet dan de POC.** De POC zegt per bron alleen "in tekst gebruikt"; hier staat hoe
 * vaak, en elke plek is een link die de sectie openklapt en naar de citatie springt (opdracht van 31
 * oktober 2026). Zoeken staat er altijd en niet pas vanaf zes bronnen, want het paneel is ook de plek
 * waar u een bron terugvindt om te wijzigen.
 *
 * **Wijzigen en verwijderen** gaan langs dezelfde regels als in de kiezer: in elke kopie die de lezer
 * kan bewerken, met een zin als er een buiten bereik bleef, en verwijderen alleen bij een bron die
 * nergens in de tekst staat, na een bevestiging.
 */

export interface SourcesLayer {
  readonly laag: 'invoer' | 'toets'
  readonly bronnen: readonly Source[]
  /** Per bron-id de plekken in de tekst, in formuliervolgorde. */
  readonly plaatsen: ReadonlyMap<string, readonly CitationPlace[]>
  /** Of de lezer in minstens één sectie van de laag kan schrijven: anders geen Wijzigen en Verwijderen. */
  readonly beheren: boolean
}

export interface SourcesOverviewProps {
  readonly taal: Language
  readonly lagen: readonly SourcesLayer[]
  /** De naam van een plek: het veld en de sectie, "Samenvatting in Inleiding". */
  readonly plaatsNaam: (place: CitationPlace) => string
  readonly onSpring: (place: CitationPlace) => void
  readonly onWijzig: (laag: 'invoer' | 'toets', sourceId: string, source: NewSource) => { readonly buitenBereik: boolean }
  readonly onVerwijder: (laag: 'invoer' | 'toets', sourceId: string) => boolean
}

const LAYER_NAME: Readonly<Record<'invoer' | 'toets', { nl: string; en: string }>> = {
  invoer: { nl: 'Bronnen in de aanvraag', en: 'Sources in the application' },
  toets: { nl: 'Bronnen in de toets', en: 'Sources in the review' },
}
const SUMMARY = (total: number, used: number) => ({
  nl: `${String(total)} ${total === 1 ? 'bron' : 'bronnen'}, ${String(used)} in de tekst`,
  en: `${String(total)} ${total === 1 ? 'source' : 'sources'}, ${String(used)} in the text`,
})
const TIMES = (count: number) => ({
  nl: count === 1 ? '1 keer in de tekst' : `${String(count)} keer in de tekst`,
  en: count === 1 ? 'once in the text' : `${String(count)} times in the text`,
})
const NOT_USED = { nl: 'Niet in de tekst', en: 'Not in the text' }
const PLACES = { nl: 'Waar deze bron staat', en: 'Where this source is cited' }
const GO_TO = (number: number, name: string) => ({ nl: `Citatie ${String(number)}: ${name}`, en: `Citation ${String(number)}: ${name}` })
const PARTLY = {
  nl: 'Gewijzigd. In een sectie die u niet kunt bewerken blijft de oude beschrijving staan.',
  en: 'Changed. In a section you cannot edit, the old description stays.',
}
const CHANGED = { nl: 'De bron is gewijzigd.', en: 'The source has been changed.' }
const REMOVED = { nl: 'De bron is verwijderd.', en: 'The source has been removed.' }
const REMOVE_QUESTION = (title: string) => ({ nl: `${title} verwijderen?`, en: `Remove ${title}?` })
const REMOVE_CONSEQUENCE = {
  nl: 'De bron staat nergens in de tekst. Hij verdwijnt uit de bronnen van dit formulier.',
  en: 'The source is not cited anywhere. It disappears from the sources of this form.',
}
const REMOVE_PROCEED = { nl: 'Bron verwijderen', en: 'Remove source' }
const CANCEL = { nl: 'Annuleren', en: 'Cancel' }
const SAVING = { nl: 'Bezig met opslaan…', en: 'Saving…' }

export function SourcesOverview({ taal: language, lagen: layers, plaatsNaam: placeName, onSpring: onJump, onWijzig: onEdit, onVerwijder: onRemove }: SourcesOverviewProps): ReactNode {
  const [query, setQuery] = useState('')
  const [notice, setNotice] = useState('')
  const searchId = useId()
  const all = layers.flatMap((layer) => layer.bronnen)

  if (all.length === 0) {
    return (
      <div className="ef-bronoverzicht">
        <p className="ef-bronoverzicht__leeg">{inLanguage(NO_SOURCES, language)}</p>
        <p className="ef-bronoverzicht__hulp">
          {inLanguage({ nl: 'Een bron citeert u in een tekstveld, met @ of de knop Bron citeren.', en: 'You cite a source in a text field, with @ or the Cite source button.' }, language)}
        </p>
      </div>
    )
  }

  const used = layers.reduce((sum, layer) => sum + layer.bronnen.filter((source) => (layer.plaatsen.get(source.id)?.length ?? 0) > 0).length, 0)
  return (
    <div className="ef-bronoverzicht">
      <p className="ef-bronoverzicht__telling">{inLanguage(SUMMARY(all.length, used), language)}</p>
      <label className="ef-bronoverzicht__zoek" htmlFor={searchId}>
        {inLanguage(SEARCH, language)}
      </label>
      <input id={searchId} className="ef-invoer" type="search" value={query} onChange={(event) => setQuery(event.target.value)} />
      <p className="ef-bronoverzicht__status" role="status">
        {notice}
      </p>
      {layers
        .filter((layer) => layer.bronnen.length > 0)
        .map((layer) => {
          const shown = [...layer.bronnen]
            .filter((source) => matches(source, query))
            .sort((a, b) => {
              const byUse = (layer.plaatsen.get(b.id)?.length ?? 0) - (layer.plaatsen.get(a.id)?.length ?? 0)
              return byUse !== 0 ? Math.sign(byUse) : a.title.localeCompare(b.title, language)
            })
          return (
            <section key={layer.laag} className="ef-bronoverzicht__laag" aria-label={inLanguage(LAYER_NAME[layer.laag], language)}>
              {layers.filter((other) => other.bronnen.length > 0).length > 1 ? (
                <h3 className="ef-bronoverzicht__laagkop">{inLanguage(LAYER_NAME[layer.laag], language)}</h3>
              ) : null}
              {shown.length === 0 ? (
                <p className="ef-bronoverzicht__leeg">{inLanguage(NOT_FOUND, language)}</p>
              ) : (
                <ul className="ef-bronoverzicht__lijst">
                  {shown.map((source) => (
                    <SourceRow
                      key={source.id}
                      bron={source}
                      taal={language}
                      plaatsen={layer.plaatsen.get(source.id) ?? []}
                      beheren={layer.beheren}
                      plaatsNaam={placeName}
                      onSpring={onJump}
                      onWijzig={(changed) => {
                        const { buitenBereik } = onEdit(layer.laag, source.id, changed)
                        setNotice(inLanguage(buitenBereik ? PARTLY : CHANGED, language))
                      }}
                      onVerwijder={() => {
                        if (onRemove(layer.laag, source.id)) setNotice(inLanguage(REMOVED, language))
                      }}
                    />
                  ))}
                </ul>
              )}
            </section>
          )
        })}
    </div>
  )
}

function SourceRow({
  bron: source,
  taal: language,
  plaatsen: places,
  beheren: manage,
  plaatsNaam: placeName,
  onSpring: onJump,
  onWijzig: onEdit,
  onVerwijder: onRemove,
}: {
  readonly bron: Source
  readonly taal: Language
  readonly plaatsen: readonly CitationPlace[]
  readonly beheren: boolean
  readonly plaatsNaam: (place: CitationPlace) => string
  readonly onSpring: (place: CitationPlace) => void
  readonly onWijzig: (source: NewSource) => void
  readonly onVerwijder: () => void
}): ReactNode {
  const [editing, setEditing] = useState(false)
  const [asking, setAsking] = useState(false)
  const removeButton = useRef<HTMLButtonElement>(null)
  const row = useRef<HTMLLIElement>(null)
  const meta = authorYear(source)
  const placesId = useId()
  return (
    <li ref={row} className="ef-bronoverzicht__bron">
      <p className="ef-bronoverzicht__titel">{source.title}</p>
      {meta === '' ? null : <p className="ef-bronoverzicht__meta">{meta}</p>}
      <p className="ef-bronoverzicht__gebruik" id={placesId}>
        {inLanguage(places.length === 0 ? NOT_USED : TIMES(places.length), language)}
      </p>
      {places.length === 0 ? null : (
        <ol className="ef-bronoverzicht__plekken" aria-label={`${inLanguage(PLACES, language)}: ${source.title}`}>
          {places.map((place, index) => (
            <li key={`${place.citatieId}-${String(index)}`}>
              <button type="button" className="ef-bronoverzicht__plek" onClick={() => onJump(place)}>
                {inLanguage(GO_TO(index + 1, placeName(place)), language)}
              </button>
            </li>
          ))}
        </ol>
      )}
      {/* Wijzigen gaat in een dialoog, dus de knoppen blijven staan en de focus kan terug naar Wijzigen. */}
      {!manage || asking ? null : (
        <div className="ef-bronoverzicht__beheer">
          <button type="button" className="ef-actieknop" data-soort="secundair" aria-haspopup="dialog" aria-label={`${inLanguage(EDIT, language)}: ${source.title}`} onClick={() => setEditing(true)}>
            {inLanguage(EDIT, language)}
          </button>
          {places.length > 0 ? null : (
            <button ref={removeButton} type="button" className="ef-actieknop" data-soort="gevaar" aria-label={`${inLanguage(REMOVE, language)}: ${source.title}`} onClick={() => setAsking(true)}>
              {inLanguage(REMOVE, language)}
            </button>
          )}
        </div>
      )}
      {!editing ? null : (
        <SourceEditForm
          bron={source}
          taal={language}
          onOpslaan={(changed) => {
            onEdit(changed)
            setEditing(false)
            // Zien wat er is gebeurd: de bron licht even op in de lijst (opdracht van 1 november 2026).
            highlight(row.current)
          }}
          onAnnuleer={() => setEditing(false)}
        />
      )}
      {!asking ? null : (
        <ConfirmQuestion
          kop={inLanguage(REMOVE_QUESTION(source.title), language)}
          consequence={inLanguage(REMOVE_CONSEQUENCE, language)}
          proceed={inLanguage(REMOVE_PROCEED, language)}
          soort="gevaar"
          cancel={inLanguage(CANCEL, language)}
          onProceed={() => {
            setAsking(false)
            onRemove()
          }}
          onCancel={() => {
            setAsking(false)
            requestAnimationFrame(() => removeButton.current?.focus())
          }}
        />
      )}
    </li>
  )
}

/**
 * Titel, type, eerste auteur en jaar van een bestaande bron wijzigen; de rest blijft (`describeSource`).
 *
 * Een modale dialoog "Bron wijzigen" sinds de opdracht van 1 november 2026 (`shell/Dialog.tsx`); tot
 * dan klapte het formulier open onder de bron in het zijpaneel. Escape en Annuleren sluiten alleen de
 * dialoog en niet het paneel, en de focus gaat terug naar Wijzigen.
 */
function SourceEditForm({
  bron: source,
  taal: language,
  onOpslaan: onSave,
  onAnnuleer: onCancel,
}: {
  readonly bron: Source
  readonly taal: Language
  readonly onOpslaan: (source: NewSource) => void
  readonly onAnnuleer: () => void
}): ReactNode {
  const issued = source.issued?.['date-parts']?.[0]?.[0]
  const [title, setTitle] = useState(source.title)
  const [type, setType] = useState<SourceType>(source.type)
  const [lastName, setLastName] = useState(source.author?.[0]?.family ?? '')
  const [firstName, setFirstName] = useState(source.author?.[0]?.given ?? '')
  const [year, setYear] = useState(issued === undefined ? '' : String(issued))
  const described = useMemo(() => describeSource(source, { title, type, lastName, firstName, year }), [source, title, type, lastName, firstName, year])
  return (
    // Escape sluit alleen de dialoog en niet het zijpaneel eromheen.
    <div className="ef-dialooghouder" onKeyDown={keepEscapeInDialog}>
    <Dialog
      taal={language}
      titel={inLanguage(EDIT_SOURCE, language)}
      className="ef-bronoverzicht__wijzig"
      onSluit={onCancel}
      secundair={{ label: inLanguage(CANCEL, language) }}
      primair={{ label: inLanguage(SAVE_EDIT, language), bezigTekst: inLanguage(SAVING, language), uitgeschakeld: described === undefined, onActie: () => described !== undefined && onSave(described) }}
    >
      <p className="ef-bronoverzicht__meta">{source.title}</p>
      <label className="ef-bronnen-kiezer__veld">
        <span>{inLanguage(TITLE, language)}</span>
        <input type="text" value={title} data-autofocus onChange={(event) => setTitle(event.target.value)} />
      </label>
      <label className="ef-bronnen-kiezer__veld">
        <span>{inLanguage(TYPE, language)}</span>
        <select value={type} onChange={(event) => setType(event.target.value as SourceType)}>
          {SOURCE_TYPES.map((candidate) => (
            <option key={candidate} value={candidate}>
              {inLanguage(TYPE_LABELS[candidate], language)}
            </option>
          ))}
        </select>
      </label>
      <label className="ef-bronnen-kiezer__veld">
        <span>{inLanguage(LAST_NAME, language)}</span>
        <input type="text" value={lastName} onChange={(event) => setLastName(event.target.value)} />
      </label>
      <label className="ef-bronnen-kiezer__veld">
        <span>{inLanguage(FIRST_NAME, language)}</span>
        <input type="text" value={firstName} onChange={(event) => setFirstName(event.target.value)} />
      </label>
      <label className="ef-bronnen-kiezer__veld">
        <span>{inLanguage(YEAR, language)}</span>
        <input type="number" inputMode="numeric" value={year} onChange={(event) => setYear(event.target.value)} />
      </label>
    </Dialog>
    </div>
  )
}
